import { Location } from '@medplum/fhirtypes';
import { useCallback, useState } from 'react';
import { getLocationTimezone } from '../utils/availability';
import { getBrowserTimezone } from '../utils/common';

interface LocationTimezoneResolution {
  locationTimezone: string | null;
  fallbackTimezone: string;
}

interface VisitTypeTimezoneResolution {
  timezone: string;
}

/**
 * Manages the relationship between location selection, visit type, and timezone.
 *
 * Both AppointmentDrawer and SharedAvailabilityFields need to:
 * - Derive a timezone (+ display suffix) when a location is selected
 * - Reset the timezone when the visit type changes (virtual → browser TZ, in-person → location TZ)
 * - Update the display suffix when the user manually picks a timezone
 *
 * This hook centralises that logic so callers only deal with the resolved values.
 */
export function useLocationTimezone() {
  const browserTimezone = getBrowserTimezone();
  const [timezoneSuffix, setTimezoneSuffix] = useState<string | undefined>(undefined);

  const resolveLocationChange = useCallback(
    (newLocation: Location | null): LocationTimezoneResolution => {
      const locationTimezone = getLocationTimezone(newLocation);
      setTimezoneSuffix(locationTimezone ? 'Location timezone' : 'Your timezone');
      return { locationTimezone, fallbackTimezone: locationTimezone ?? browserTimezone };
    },
    [browserTimezone]
  );

  const resolveVisitTypeChange = useCallback(
    (isVirtual: boolean, currentLocation: Location | null, currentTimezone?: string): VisitTypeTimezoneResolution => {
      if (isVirtual) {
        const effectiveTimezone = currentTimezone ?? browserTimezone;
        setTimezoneSuffix(effectiveTimezone === browserTimezone ? 'Your timezone' : undefined);
        return { timezone: effectiveTimezone };
      }
      const locationTimezone = currentLocation ? getLocationTimezone(currentLocation) : null;
      setTimezoneSuffix(locationTimezone ? 'Location timezone' : undefined);
      return { timezone: locationTimezone || '' };
    },
    [browserTimezone]
  );

  const resolveTimezoneSelection = useCallback(
    (value: string) => {
      setTimezoneSuffix(value === browserTimezone ? 'Your timezone' : undefined);
    },
    [browserTimezone]
  );

  const reset = useCallback(() => {
    setTimezoneSuffix('Your timezone');
  }, []);

  return {
    browserTimezone,
    timezoneSuffix,
    resolveLocationChange,
    resolveVisitTypeChange,
    resolveTimezoneSelection,
    reset,
  };
}
